import { toEpochSeconds } from '@shared/utils/epoch.util';
import type { RawEntry } from '../models/panel-entry.model';

/**
 * Primitif normalisasi response panel profil vendor.
 *
 * **Tanpa layer ini semua validator salah.** BE mengirim field bersarang sebagai STRING berisi
 * JSON dan boolean sebagai string `"true"`. Menulis `entry.is_primary === true` selalu `false`;
 * menulis `entry.deed_type.ident_name` crash.
 *
 * Lihat plans/KONSEP-PANEL-VALIDATION.md §3.1.
 */

/**
 * Field bersarang berupa string JSON → objek.
 *
 * `"{}"` dan `null` sama-sama jadi `null`. Ini yang membuat semua cek kekosongan seragam:
 * `fax_number: "{}"` berarti user tidak mengisi faks — kalau dibiarkan jadi `{}` (truthy),
 * `if (entry.faxNumber)` lolos padahal kosong.
 */
export function parseObj<T = Record<string, unknown>>(raw: unknown): T | null {
  if (raw == null) return null;
  // Jaga-jaga bila BE suatu saat mengirim objek betulan.
  if (typeof raw === 'object') return Object.keys(raw).length === 0 ? null : (raw as T);
  if (typeof raw !== 'string') return null;

  const s = raw.trim();
  if (s === '' || s === '{}' || s === 'null') return null;

  try {
    const parsed: unknown = JSON.parse(s);
    if (parsed == null || typeof parsed !== 'object') return null;
    if (Object.keys(parsed).length === 0) return null;
    return parsed as T;
  } catch {
    // Bukan error HTTP — ini kontrak BE yang berubah. Dicatat ke console supaya ada jejaknya;
    // JANGAN ditambahi toast (interceptor hanya menangani error HTTP).
    console.error('[panel-normalize] gagal parse objek:', raw);
    return null; // fail-safe: dianggap kosong → panel ⚠️, bukan lolos diam-diam
  }
}

/** Field array (mis. `files`) berupa string JSON berisi array. */
export function parseArr<T = Record<string, unknown>>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (raw == null) return [];
  if (typeof raw !== 'string') return [];

  const s = raw.trim();
  if (s === '' || s === '[]' || s === 'null') return [];

  try {
    const parsed: unknown = JSON.parse(s);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    console.error('[panel-normalize] gagal parse array:', raw);
    return [];
  }
}

/**
 * Boolean-as-string → boolean.
 *
 * INI jebakan paling mematikan di seluruh fitur: `"true" === true` bernilai `false` di
 * JavaScript, jadi panel PIC & Susunan Pengurus TIDAK AKAN PERNAH valid tanpa fungsi ini.
 */
export function parseBool(raw: unknown): boolean {
  return raw === true || raw === 1 || raw === 'true' || raw === '1';
}

/** Ambil `ident_name` dari field objek bersarang. */
export function identOf(raw: unknown): string | null {
  return parseObj<{ ident_name?: string }>(raw)?.ident_name ?? null;
}

/** String non-kosong, selain itu `null`. String kosong = tidak diisi, bukan nilai. */
export function str(raw: unknown): string | null {
  return typeof raw === 'string' && raw.trim() !== '' ? raw : null;
}

/**
 * Skalar yang BE kirim **kadang string, kadang number**, tapi yang bukan angka untuk dihitung.
 *
 * `str()` sendiri membuang `number` jadi `null` — sengaja, karena semua pemakainya selama ini
 * memang field teks. `profile_tools` adalah panel pertama yang tidak konsisten pada field
 * non-tanggal: `qty` datang sebagai `"1"` di endpoint LIST dan `1` di endpoint DETAIL.
 *
 * **Bukan `num()`.** Nilai di sini boleh membawa pemisah ribuan/desimal (`"1.000,50"`) yang bukan
 * angka JavaScript yang sah; `num()` mengubahnya jadi `NaN` → `null`, dan selnya sekadar menulis
 * "-" tanpa satu pun error.
 */
export function strOrNum(raw: unknown): string | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? String(raw) : null;
  return str(raw);
}

/**
 * Field TANGGAL → epoch DETIK. **Pakai ini, bukan `num()`, untuk setiap field tanggal.**
 *
 * `num()` menyerah pada string ISO: `Number('2018-12-31')` = `NaN` → `null`, dan selnya menulis
 * "-" tanpa satu pun error. Response LIST `profile_financial_statements` pernah benar-benar
 * memuat `"2018-12-31"` (PLAN-FINANCIAL-STATEMENTS.md D5), jadi ini bukan kemungkinan teoretis.
 *
 * Konversinya di `@shared/utils/epoch.util` — satu-satunya tempat aritmetika epoch.
 */
export function epochNum(raw: unknown): number | null {
  return toEpochSeconds(raw);
}

/**
 * Angka non-tanggal. Untuk field TANGGAL pakai {@link epochNum} — fungsi ini menolak string ISO.
 *
 * String numerik ikut diterima: BE tidak konsisten mengirim angka sebagai number di semua
 * endpoint (mis. `qty` = `"1"` di LIST, `1` di DETAIL).
 */
export function num(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Baris mentah apa pun → `RawEntry` yang aman dibaca (bukan objek → objek kosong). */
export function toRawEntry(raw: unknown): RawEntry {
  return raw != null && typeof raw === 'object' ? (raw as RawEntry) : {};
}
